import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { AsyncView, Button, Card, Checkbox, Notice, Stat, useToast } from '../ui';
import { useResource } from '../lib/useResource';
import { podeAtuar, useSessao } from '../lib/session';
import { api, ApiError } from '../lib/api';

interface Fila { autoAssignmentEnabled: boolean }

/**
 * Configurações do workspace.
 *
 * A tela anterior tinha um interruptor que salvava no `change` — sem confirmação,
 * sem indicação de que salvou, e sem como desfazer. Distribuição automática muda
 * para onde vai toda conversa nova; é uma decisão que merece um passo explícito.
 *
 * Aqui a mudança fica pendente até o "Salvar", e o botão só existe quando há
 * algo a salvar. Isso também resolve o caso de rede lenta: antes, dois cliques
 * rápidos disparavam duas requisições e o estado final dependia de qual
 * chegasse por último.
 */
export function Configuracoes() {
  const { agent, tenant } = useSessao();
  const toast = useToast();
  const podeEditar = podeAtuar(agent.role, 'ADMIN');
  const { state, recarregar } = useResource<Fila>('/workspace/queue/settings', { vazio: () => false });

  const [pendente, setPendente] = useState<boolean | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Recarregar descarta o que estava pendente: o valor do servidor é a verdade.
  useEffect(() => { if (state.status === 'ready') setPendente(null); }, [state]);

  const salvo = state.status === 'ready' ? state.data.autoAssignmentEnabled : false;
  const valor = pendente ?? salvo;
  const mudou = pendente !== null && pendente !== salvo;

  async function salvar() {
    setSalvando(true);
    try {
      await api('/workspace/queue/settings', { method: 'PATCH', body: { autoAssignmentEnabled: valor } });
      recarregar();
      toast(valor ? 'Distribuição automática ligada.' : 'Distribuição automática desligada.');
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Não foi possível salvar.', 'danger');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <Card title="Workspace" as="h2">
        <div className="stats">
          <Stat rotulo="Nome" valor={<span style={{ fontSize: 'var(--text-lg)' }}>{tenant.name}</span>} />
          <Stat rotulo="Identificador" valor={<span style={{ fontSize: 'var(--text-lg)' }}>{tenant.slug}</span>} />
          <Stat rotulo="Seu papel" valor={<span style={{ fontSize: 'var(--text-lg)' }}>{
            { ADMIN: 'Administrador', SUPERVISOR: 'Supervisor', AGENT: 'Atendente' }[agent.role]
          }</span>} />
        </div>
      </Card>

      <Card title="Integração com Lead Machine" as="h2">
        <p style={{ marginTop: 0, color: 'var(--text-muted)' }}>
          Use este identificador em Integrações e eventos → Setter WA no Lead Machine. Ele vincula os contatos e registra no CRM as mensagens recebidas e enviadas.
        </p>
        <div className="stats">
          <Stat rotulo="Tenant ID do Setter WA" valor={<code style={{ fontSize: 'var(--text-sm)' }}>{tenant.id}</code>} />
        </div>
      </Card>

      <Card title="Fila de atendimento" as="h2">
        <AsyncView
          state={state}
          label="as configurações da fila"
          skeletonRows={1}
          empty={{ title: 'Sem configuração', text: 'A fila usa os padrões do sistema.' }}
        >
          {() => (
            <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
              <Checkbox
                checked={valor}
                disabled={!podeEditar || salvando}
                onChange={(e) => setPendente(e.currentTarget.checked)}
              >
                Distribuir conversas novas automaticamente
              </Checkbox>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: 0 }}>
                {valor
                  ? 'Cada conversa nova é atribuída ao operador disponível com menos conversas abertas.'
                  : 'Conversas novas ficam sem responsável até alguém assumir na fila.'}
              </p>
              {/* O botão só aparece quando há mudança para salvar. Um "Salvar"
                  permanentemente ativo não distingue "salvo" de "não salvo". */}
              {mudou && (
                <div>
                  <Button
                    variant="primary"
                    onClick={() => void salvar()}
                    loading={salvando}
                    icon={<Check size={18} aria-hidden="true" />}
                  >
                    Salvar alteração
                  </Button>
                  <Button variant="tertiary" onClick={() => setPendente(null)}>Descartar</Button>
                </div>
              )}
            </div>
          )}
        </AsyncView>
      </Card>

      {!podeEditar && (
        <Notice tone="info">
          As configurações do workspace são editáveis apenas por administradores.
        </Notice>
      )}
    </div>
  );
}
